import { Trans, useTranslation } from "react-i18next"
import { Flex } from "../../../../components/flex/Flex"
import { Spacer } from "../../../../components/spacer/Spacer"
import Text from "../../../../components/Text"
import { useIsDesktopVersion } from "../../../../hooks/mediaHooks"
import { theme } from "../../../../styles/stitches.config"
import ChartLegend from "../../../../components/chart/ChartLegend"
import { DistributionTariffSelect } from "../DistributionTariffSelect"
import { MarketPriceChartResponsive } from "./MarketPriceChart"
import Separator from "../../../../components/Separator"
import InfoBox from "../../../../components/info/InfoBox"
import { SCard, SCardItem, SFooterContainer, SHeaderContainer } from "./AnnualMarketPrice.styled"
import ChartPlaceholder from "../../../../components/placeholder/components/ChartPlaceholder"
import { PageContainer, ConfigContainer } from "../containers"
import { ValueInput } from "../ValueInput"
import { CalculationLabel } from "../CalculationLabel"
import { InputSetContainer } from "../input/InputSetContainer"
import { getCurrency } from "../../../../i18n/config"
import { FormattedUnit } from "../../../../components/FormattedUnit"
import { useAnnualPriceController } from "./useAnnualPriceController"
import { ChartLegendContainer } from "../ChartLegendContainer"
import { EnergyUnit } from "@nano-portal/shared"

const Header = () => {
  const { t } = useTranslation(["microsite"])
  const isDesktop = useIsDesktopVersion()

  return (
    <Flex>
      {isDesktop && <Spacer size={theme.space.s16} />}
      <Flex direction="column">
        <Text type="headerH2">
          <Trans t={t} i18nKey="microsite:market_price_header">
            <Text type="headerH2Positive" />
          </Trans>
        </Text>

        <Text type="labelsAlt">{t("microsite:market_price_header_label")}</Text>
      </Flex>
    </Flex>
  )
}

interface SummaryProps {
  lastSixMonths: number
  nextSixMonths: number
  total: number
}

const Summary = ({ lastSixMonths, nextSixMonths, total }: SummaryProps) => {
  const { t } = useTranslation(["microsite"])
  const isDesktop = useIsDesktopVersion()

  return (
    <SCard>
      <SCardItem>
        <Text type="textsLarge">
          <Trans t={t} i18nKey="microsite:last_six_months">
            <br />
          </Trans>
        </Text>
        <Spacer size={theme.space.s6} />
        <FormattedUnit
          value={lastSixMonths}
          valueTextType="headerH2"
          unit={getCurrency().symbol}
          unitTextType="headerH5"
          maxFractionDigits={0}
        />
      </SCardItem>

      {isDesktop && (
        <Flex direction="column">
          <Separator orientation="vertical" />
        </Flex>
      )}

      <SCardItem>
        <Text type="textsLarge">
          <Trans t={t} i18nKey="microsite:next_six_months">
            <br />
          </Trans>
          <InfoBox inline css={{ marginLeft: theme.space.s2 }}>
            <Text type="textsLarge">{t("microsite:next_six_months_tooltip_1")}</Text>
          </InfoBox>
        </Text>
        <Spacer size={theme.space.s6} />

        <FormattedUnit
          value={nextSixMonths}
          valueTextType="headerH2Neutral"
          unit={getCurrency().symbol}
          unitTextType="headerH5Neutral"
          maxFractionDigits={0}
        />
      </SCardItem>

      {isDesktop && (
        <Flex direction="column">
          <Separator orientation="vertical" />
        </Flex>
      )}

      <SCardItem>
        <Text type="textsLarge">{t("microsite:total")}</Text>
        <Spacer size={theme.space.s6} />

        <FormattedUnit
          value={total}
          valueTextType="headerH2Positive"
          unit={getCurrency().symbol}
          unitTextType="headerH5Positive"
          maxFractionDigits={0}
        />
      </SCardItem>
    </SCard>
  )
}

export const AnnualMarketPrice = () => {
  const { t } = useTranslation(["microsite"])
  const isDesktop = useIsDesktopVersion()
  const controller = useAnnualPriceController()

  return (
    <PageContainer>
      <SHeaderContainer>
        <Header />

        {!isDesktop && <Spacer size={theme.space.s6} />}

        <ConfigContainer>
          <InputSetContainer>
            <ValueInput
              inputLabel={t("microsite:annual_consumption")}
              valueLabel={EnergyUnit.MWh}
              value={controller.annualConsumptionInMwh}
              onChange={controller.setAnnualConsumptionInMwh}
            />

            <Spacer size={theme.space.s4} />
            <DistributionTariffSelect
              value={controller.distributionTariff}
              onValueChange={controller.setDistributionTariff}
            />
          </InputSetContainer>

          <Spacer size={theme.space.s6} />

          <CalculationLabel />
        </ConfigContainer>
      </SHeaderContainer>

      {!isDesktop && <Spacer size={theme.space.s6} />}

      {controller.isLoading ? (
        <ChartPlaceholder />
      ) : (
        <>
          <MarketPriceChartResponsive isErrorState={controller.isError} data={controller.chartData} />

          <ChartLegendContainer>
            <ChartLegend
              items={[
                {
                  color: "primary",
                  label: t("microsite:historical_market_price"),
                  hidden: controller.hidePastMonths,
                  onClick: controller.onPastLegendClick,
                },
                {
                  color: "lightGreen",
                  label: t("microsite:predicted_market_price"),
                  hidden: controller.hideFutureMonths,
                  onClick: controller.onFutureLegendClick,
                  tooltipContent: (
                    <Text type="textsLarge">
                      <Trans t={t} i18nKey="microsite:next_six_months_tooltip_2">
                        <Text type="menuItemActive" />
                      </Trans>
                    </Text>
                  ),
                },
              ]}
            />
          </ChartLegendContainer>

          <Spacer size={theme.space.s10} />

          <SFooterContainer>
            <Summary lastSixMonths={controller.pastSum} nextSixMonths={controller.futureSum} total={controller.total} />
          </SFooterContainer>
        </>
      )}

      <Spacer size={theme.space.s6} />
    </PageContainer>
  )
}
